import { useState, useEffect } from 'react';
import { Button, TextInput, Select } from '@mantine/core';
import CardModal from './card-modal';
import { useTranslation } from 'react-i18next';
import { useUpdateBusiness } from '@/hooks/api-hooks';
import { useActiveBusiness } from '@/pages/wrapper';
import CountrySelector from './country-selector';

export default function ForceBusinessData() {
    const { t } = useTranslation();
    const { activeBusiness } = useActiveBusiness();
    const updateBusiness = useUpdateBusiness();

    const [taxName, setTaxName] = useState('');
    const [taxIdType, setTaxIdType] = useState<string | null>('');
    const [taxIdNumber, setTaxIdNumber] = useState('');
    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [province, setProvince] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [countryCode, setCountryCode] = useState('');

    const [isSaving, setIsSaving] = useState(false);
    const [localOpened, setLocalOpened] = useState(true);

    // Sync state when activeBusiness is loaded or changed
    useEffect(() => {
        if (activeBusiness) {
            setTaxName(activeBusiness.tax_name || '');
            setTaxIdType(activeBusiness.tax_id_type || '');
            setTaxIdNumber(activeBusiness.tax_id_number || '');
            setAddress(activeBusiness.address || '');
            setCity(activeBusiness.city || '');
            setProvince(activeBusiness.province || '');
            setPostalCode(activeBusiness.postal_code || '');
            setCountryCode(activeBusiness.country_code || '');
            setLocalOpened(true); // reset localOpened on business change
        }
    }, [activeBusiness]);

    if (!activeBusiness) return null;

    const isMissingFields = !activeBusiness.tax_name ||
        !activeBusiness.tax_id_type ||
        !activeBusiness.tax_id_number ||
        !activeBusiness.address ||
        !activeBusiness.city ||
        !activeBusiness.province ||
        !activeBusiness.postal_code ||
        !activeBusiness.country_code;

    const opened = isMissingFields && localOpened;

    const handleSave = async () => {
        if (!taxName || !taxIdType || !taxIdNumber || !address || !city || !province || !postalCode || !countryCode) {
            return;
        }
        setIsSaving(true);
        try {
            await updateBusiness.mutateAsync({
                id: activeBusiness.id,
                tax_name: taxName,
                tax_id_type: taxIdType,
                tax_id_number: taxIdNumber,
                address,
                city,
                province,
                postal_code: postalCode,
                country_code: countryCode,
            });
            setLocalOpened(false);
        } catch (error) {
            console.error(error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <CardModal
            header={<div className='font-bold'>{t('complete_business_data')}: {activeBusiness?.name}</div>}
            opened={opened}
            size='lg'
            closeOnEscape={false}
            withCloseButton={false}
            onClose={() => { }}
        >
            <div className="flex flex-col gap-4">
                <div className="text-sm text-gray-500">
                    {t('complete_business_data_description')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextInput
                        required
                        label={t('tax_name')}
                        value={taxName}
                        onChange={(e) => setTaxName(e.target.value)}
                        disabled={isSaving}
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <Select
                            required
                            label={t('tax_id_type')}
                            data={[
                                { value: 'CUIL', label: 'CUIL' },
                                { value: 'CUIT', label: 'CUIT' },
                                { value: 'DNI', label: 'DNI' },
                                { value: 'Pasaporte', label: 'Pasaporte' },
                                { value: 'Otro', label: 'Otro' }
                            ]}
                            value={taxIdType}
                            onChange={(val) => setTaxIdType(val)}
                            disabled={isSaving}
                        />
                        <TextInput
                            required
                            label={t('tax_id_number')}
                            value={taxIdNumber}
                            onChange={(e) => setTaxIdNumber(e.target.value)}
                            disabled={isSaving}
                        />
                    </div>
                    <TextInput
                        required
                        label={t('address')}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        disabled={isSaving}
                    />
                    <TextInput
                        required
                        label={t('city')}
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        disabled={isSaving}
                    />
                    <TextInput
                        required
                        label={t('province')}
                        value={province}
                        onChange={(e) => setProvince(e.target.value)}
                        disabled={isSaving}
                    />
                    <CountrySelector
                        className="w-full"
                        value={countryCode}
                        onChange={(val) => setCountryCode(val || '')}
                        disabled={isSaving}
                    />
                    <TextInput
                        required
                        label={t('postal_code')}
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        disabled={isSaving}
                    />
                </div>
                <div className="flex justify-end w-full mt-4">
                    <Button
                        color="black"
                        radius="xl"
                        onClick={handleSave}
                        disabled={!taxName || !taxIdType || !taxIdNumber || !address || !city || !province || !postalCode || !countryCode || isSaving}
                        loading={isSaving}
                    >
                        {t('save')}
                    </Button>
                </div>
            </div>
        </CardModal>
    );
}
